import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Company, AuthContextType } from '../types';
import airtableService from '../services/airtable';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userCompany, setUserCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize access control provider when auth state changes
  useEffect(() => {
    if (user || userCompany) {
      airtableService.setAccessControlProvider({
        getUserCompanyId: () => {
          if (user?.userGroupType === 'Company User') {
            return userCompany?.id || user?.companyId || null;
          }
          return null;
        },
        canAccessAllData: () => {
          return user?.userGroupType === 'Administrator';
        }
      });
    }
  }, [user, userCompany]);

  useEffect(() => {
    // Check for stored auth token and validate
    const checkAuthStatus = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const userData = localStorage.getItem('userData');

        if (userId && userData) {
          // Validate the user still exists and is active
          try {
            const response = await airtableService.getUserById(userId);
            if (response.success && response.data.active) {
              setUser(response.data);

              // Load user's company if they have one
              const companyId = localStorage.getItem('userCompanyId');
              if (companyId) {
                try {
                  const companyResponse = await airtableService.getCompanyById(companyId);
                  if (companyResponse.success) {
                    setUserCompany(companyResponse.data);
                  }
                } catch (error) {
                  console.warn('Could not load user company:', error);
                }
              }
            } else {
              // User not found or inactive, clear storage
              localStorage.removeItem('userId');
              localStorage.removeItem('userData');
              localStorage.removeItem('userCompanyId');
            }
          } catch (error) {
            // If validation fails, use stored data but log warning
            console.warn('Could not validate user, using cached data:', error);
            setUser(JSON.parse(userData));

            // Try to load company data from cache
            const companyData = localStorage.getItem('userCompanyData');
            if (companyData) {
              try {
                setUserCompany(JSON.parse(companyData));
              } catch (e) {
                console.warn('Could not parse cached company data:', e);
              }
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('userId');
        localStorage.removeItem('userData');
        localStorage.removeItem('userCompanyId');
        localStorage.removeItem('userCompanyData');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string, companyId?: string): Promise<void> => {
    try {
      setLoading(true);

      // Authenticate with Airtable
      const response = await airtableService.authenticateUser(email, password);

      if (!response.success) {
        throw new Error(response.error || 'Authentication failed');
      }

      const authenticatedUser = response.data;

      // Store auth data
      localStorage.setItem('userId', authenticatedUser.id);
      localStorage.setItem('userData', JSON.stringify(authenticatedUser));

      // Handle company association for Company Users
      if (companyId) {
        try {
          const companyResponse = await airtableService.getCompanyById(companyId);
          if (companyResponse.success) {
            setUserCompany(companyResponse.data);
            localStorage.setItem('userCompanyId', companyId);
            localStorage.setItem('userCompanyData', JSON.stringify(companyResponse.data));
          }
        } catch (error) {
          console.warn('Could not load company data during login:', error);
        }
      } else if (authenticatedUser.companyId) {
        // For users with pre-assigned company
        try {
          const companyResponse = await airtableService.getCompanyById(authenticatedUser.companyId);
          if (companyResponse.success) {
            setUserCompany(companyResponse.data);
            localStorage.setItem('userCompanyId', authenticatedUser.companyId);
            localStorage.setItem('userCompanyData', JSON.stringify(companyResponse.data));
          }
        } catch (error) {
          console.warn('Could not load user\'s assigned company:', error);
        }
      }

      setUser(authenticatedUser);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('userData');
    localStorage.removeItem('userCompanyId');
    localStorage.removeItem('userCompanyData');
    setUser(null);
    setUserCompany(null);
  };

  const register = async (userData: Partial<User>): Promise<void> => {
    // Registration is disabled - users must be added by admins to the Airtable Users table
    throw new Error('Registration is not available. Please contact your administrator to create an account.');
  };

  // Access control helper functions
  const isAdministrator = (): boolean => {
    return user?.userGroupType === 'Administrator';
  };

  const canAccessAllData = (): boolean => {
    return isAdministrator();
  };

  const getUserCompanyId = (): string | null => {
    // For Company Users, return their associated company ID
    if (user?.userGroupType === 'Company User') {
      return userCompany?.id || user?.companyId || null;
    }
    // For Administrators, return null (they can see all data)
    return null;
  };

  const value: AuthContextType = {
    user,
    userCompany,
    loading,
    login,
    logout,
    register,
    isAdministrator,
    canAccessAllData,
    getUserCompanyId
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};